import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { api, qs } from "@/lib/api";
import { BRANDS } from "@/lib/brands";
import { formatAgo } from "@/lib/format";
import { toCents } from "@/lib/money";
import { BrandMark } from "@/components/BrandMark";
import { Empty, PageHeader } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { INTERVALS, PRICE_TYPES, PRODUCT_KINDS, kindLabel, type Product } from "./catalog";

export function productStatus(p: Product): { label: string; variant: "success" | "muted" | "warning" } {
  if (p.archived_at) return { label: "archived", variant: "muted" };
  if (!p.active) return { label: "off", variant: "warning" };
  return { label: "on sale", variant: "success" };
}

/** Billing → Products: courses, books, events and memberships with their prices and checkout links. */
export function ProductsPage() {
  const [brand, setBrand] = useState<number | "">("");
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [creating, setCreating] = useState(false);
  const list = useQuery({
    queryKey: ["products", brand, kind, q, activeOnly],
    queryFn: () => api<{ products: Product[] }>("/api/v1/products" + qs({ brand_id: brand || undefined, kind: kind || undefined, q: q.trim() || undefined, active_only: activeOnly || undefined, limit: 500 })),
    placeholderData: (p) => p,
  });
  const rows = list.data?.products ?? [];
  return (
    <div className="mx-auto max-w-[1200px] space-y-3">
      <PageHeader
        title="Products"
        sub="What the sites and staff can sell"
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus /> New product
          </Button>
        }
      >
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Each product has one or more prices (one payment, a subscription, or a payment plan). A product with a checkout address can be bought on its brand's account site; past
          orders keep the product even after it's switched off or archived.
        </p>
      </PageHeader>
      <Card className="overflow-x-auto">
        <div className="flex flex-wrap items-center gap-2 border-b p-3">
          <Input className="h-8 w-56" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, SKU or checkout address" aria-label="Search products" />
          <Select className="h-8 w-auto" value={brand} onChange={(e) => setBrand(e.target.value ? Number(e.target.value) : "")} aria-label="Brand">
            <option value="">All brands</option>
            {BRANDS.map((b) => (
              <option key={b.id} value={b.id}>
                {b.short}
              </option>
            ))}
          </Select>
          <Select className="h-8 w-auto" value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Kind">
            <option value="">Every kind</option>
            {PRODUCT_KINDS.map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
          <label className="flex items-center gap-1.5 text-sm">
            <Checkbox checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} /> On sale only
          </label>
        </div>
        {list.isLoading && <Skeleton className="m-3 h-32" />}
        {!list.isLoading && rows.length === 0 && <Empty title={q || brand || kind ? "No products match." : "No products yet."}>Press New product to add one.</Empty>}
        {rows.length > 0 && (
          <Table>
            <THead>
              <tr>
                <TH>Product</TH>
                <TH>Brand</TH>
                <TH>Kind</TH>
                <TH>Prices</TH>
                <TH>Status</TH>
                <TH>Changed</TH>
              </tr>
            </THead>
            <tbody>
              {rows.map((p) => {
                const st = productStatus(p);
                const prices = p.prices.filter((x) => x.active);
                return (
                  <TR key={p.id} className={p.archived_at ? "row-dim" : ""}>
                    <TD className="max-w-[22rem]">
                      <Link to="/billing/products/$productId" params={{ productId: String(p.id) }} className="block truncate font-medium text-[var(--color-primary)] hover:underline">
                        {p.name}
                      </Link>
                      <div className="truncate text-xs text-[var(--color-muted-foreground)]">
                        {p.slug ? `checkout: /${p.slug}` : "no checkout page"}
                        {p.sku ? `, SKU ${p.sku}` : ""}
                        {p.keap_id ? ", from Keap" : ""}
                      </div>
                    </TD>
                    <TD>
                      <BrandMark id={p.brand_id} />
                    </TD>
                    <TD className="text-xs">{kindLabel(p.kind)}</TD>
                    <TD className="max-w-[18rem] text-xs">
                      {prices.length === 0 ? <span className="text-[var(--color-warning-fg)]">no active price</span> : prices.map((x) => x.summary).join("; ")}
                    </TD>
                    <TD>
                      <Badge variant={st.variant}>{st.label}</Badge>
                    </TD>
                    <TD className="whitespace-nowrap text-xs">{formatAgo(p.updated_at)}</TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
      {creating && <NewProductDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

/** products_create, switched off until someone turns it on (Brandon OKs anything customers can buy). */
function NewProductDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(2);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("course");
  const [type, setType] = useState("one_time");
  const [amount, setAmount] = useState("");
  const [interval, setInterval] = useState("month");
  const [installments, setInstallments] = useState("3");
  const [down, setDown] = useState("");
  const cents = toCents(amount);
  const create = useMutation({
    mutationFn: () =>
      api<Product>("/api/v1/products", {
        json: {
          brand_id: brand,
          name: name.trim(),
          kind,
          inactive: true,
          prices: [
            {
              type,
              amount_cents: cents,
              interval: type === "one_time" ? undefined : interval,
              installments: type === "payment_plan" ? Number(installments) : undefined,
              down_payment_cents: type === "payment_plan" && down ? (toCents(down) ?? undefined) : undefined,
              is_default: true,
            },
          ],
        },
      }),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ["products"] });
      toast(`Created ${p.name} (switched off)`);
      onClose();
      void navigate({ to: "/billing/products/$productId", params: { productId: String(p.id) } });
    },
    onError: (e) => toast(e.message, "error"),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(560px,calc(100vw-2rem))]">
        <DialogTitle>New product</DialogTitle>
        <DialogDescription>
          It starts switched off, with no checkout page, so nobody can buy it yet. Add the checkout address and settings on the next screen, then switch it on once Brandon says
          it's ready.
        </DialogDescription>
        <div className="space-y-3 text-sm">
          <div className="space-y-1">
            <Label htmlFor="np-name">Name</Label>
            <Input id="np-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="How to Buy a Mobile Home Park (book)" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="np-brand">Brand</Label>
              <Select id="np-brand" value={brand} onChange={(e) => setBrand(Number(e.target.value))}>
                {BRANDS.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="np-kind">Kind</Label>
              <Select id="np-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
                {PRODUCT_KINDS.map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <fieldset className="space-y-2 rounded-md border p-3">
            <legend className="px-1 text-xs font-semibold">First price</legend>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="np-type">How they pay</Label>
                <Select id="np-type" value={type} onChange={(e) => setType(e.target.value)}>
                  {PRICE_TYPES.map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="np-amount">{type === "one_time" ? "Price ($)" : type === "recurring" ? "Each period ($)" : "Each installment ($)"}</Label>
                <Input id="np-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="99" />
              </div>
              {type !== "one_time" && (
                <div className="space-y-1">
                  <Label htmlFor="np-interval">Every</Label>
                  <Select id="np-interval" value={interval} onChange={(e) => setInterval(e.target.value)}>
                    {INTERVALS.map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
              {type === "payment_plan" && (
                <>
                  <div className="space-y-1">
                    <Label htmlFor="np-inst">Installments</Label>
                    <Input id="np-inst" type="number" min={2} value={installments} onChange={(e) => setInstallments(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="np-down">Down payment today ($, optional)</Label>
                    <Input id="np-down" inputMode="decimal" value={down} onChange={(e) => setDown(e.target.value)} placeholder="0" />
                  </div>
                </>
              )}
            </div>
            {amount && cents === null && <p className="text-xs text-[var(--color-danger-fg)]">Type an amount like 99 or 29.95.</p>}
          </fieldset>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={create.isPending || !name.trim() || cents === null || cents <= 0} onClick={() => create.mutate()}>
            Create (switched off)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
